import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Chip, Tooltip } from '@mui/material';
import { getPolicyLinkMetrics } from '@/services/api';
import { formatErrorForDisplay } from '@/utils/errorUtils';
import { PolicyLinkMetricsResponse, LinkMetricsItem } from '@/types/sms';
import { formatDateTimeIST } from '@/utils/dateUtils';

interface PolicyLinkMetricsProps {
  policyNumber: string;
}

export const PolicyLinkMetrics: React.FC<PolicyLinkMetricsProps> = ({ policyNumber }) => {
  const [data, setData] = useState<PolicyLinkMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getPolicyLinkMetrics(policyNumber);
        if (isMounted) {
          setData(response);
        }
      } catch (e: any) {
        if (isMounted) {
          setError(e?.message || 'Failed to load link metrics');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [policyNumber]);


  const getLinkLabel = (item: LinkMetricsItem) =>
    item.link_type === 'renewal' ? 'Renewal Link' : 'PDF Download';

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading link activity...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  if (!data || !data.links || data.links.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No link clicks recorded yet for this policy.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2">
        Link Activity (Total Clicks: <strong>{data.total_clicks}</strong>)
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {data.links.map((item, index) => (
          <Box
            key={`${item.link_type}-${item.sms_notification_id ?? 'none'}-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip
                label={getLinkLabel(item)}
                size="small"
                color={item.link_type === 'renewal' ? 'primary' : 'secondary'}
                variant="outlined"
              />
              <Typography variant="body2">
                Clicks: <strong>{item.click_count}</strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                First Click: {formatDateTimeIST(item.first_clicked_at)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last Click: {formatDateTimeIST(item.last_clicked_at)}
              </Typography>
              <Tooltip title={item.original_url}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ maxWidth: 260 }}
                  noWrap
                >
                  URL: {item.original_url}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PolicyLinkMetrics;


